import { ChangeEvent, forwardRef, useState, useImperativeHandle, useRef } from 'react';
import { Box, Button, TextField, TextFieldProps, styled, Typography } from '@mui/material';
import './file-input.scss';

const FileInputError = styled(TextField)<TextFieldProps>(() => ({
	'& .MuiInputBase-root': {
		display: 'none',
	},
}));

interface FileInputProps {
	id: string;
	name: string;
	accept?: string;
	onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	error?: string;
	disabled?: boolean;
	buttonText?: string;
	showPreview?: boolean;
	variant?: 'contained' | 'outlined';
}

export interface FileInputRef {
	resetPreview: () => void;
}

const FileInput = forwardRef<FileInputRef, FileInputProps>(
	(
		{
			id,
			name,
			accept,
			onChange,
			error,
			disabled,
			buttonText = 'Choose File',
			showPreview = false,
			variant = 'contained',
		},
		ref
	) => {
		const [selectedFile, setSelectedFile] = useState<File | null>(null);
		const inputRef = useRef<HTMLInputElement>(null);

		const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0] || null;
			setSelectedFile(file);
			onChange?.(event);
		};

		const isImageFile = (file: File) => {
			return file.type.startsWith('image/');
		};

		const resetPreview = () => {
			setSelectedFile(null);
		};

		useImperativeHandle(ref, () => ({
			resetPreview,
		}));

		return (
			<>
				{showPreview && selectedFile && isImageFile(selectedFile) && (
					<Box className="file-preview" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<img
								src={URL.createObjectURL(selectedFile)}
								alt={selectedFile.name}
								style={{ width: 120, height: 'auto', objectFit: 'contain' }}
							/>
							<Box>
								<Typography
									component="p"
									sx={{ fontSize: '14px', color: '#000000', opacity: '0.6', marginTop: '0.5rem' }}
								>
									{selectedFile.name}
								</Typography>
							</Box>
						</Box>
					</Box>
				)}
				<Box component="label" className="file-input-wrapper">
					<Button variant={variant} className="file-input-container" disabled={disabled}>
						{buttonText}
					</Button>
					<input
						type="file"
						{...{ id, name, accept }}
						onChange={handleFileChange}
						ref={inputRef}
						disabled={disabled}
					/>
					<FileInputError error={!!error} helperText={error} />
				</Box>
			</>
		);
	}
);

FileInput.displayName = 'FileInput';

export default FileInput;
